import * as React from 'react';
import { WELCOME_BACK_URL } from '~/containers/WelcomeBack/constants';
import { Route, Switch } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
import App from '~/containers/App';
import NavigatorHeader from '~/components/NavigatorHeader';
import NavigatorFooter from '~/components/NavigatorFooter';
import QueryLoader from '~/components/Loaders/QueryLoader';
import AccountIndicator from '~/components/AccountIndicator';
import getCurrentStep from '../calculations/accessibleSteps/getCurrentStep';
import steps from '../calculations/steps';
import { RENTERS_URL } from '../constants';

// Lazily load Route components
const PropertyBasicsV2 = React.lazy(
  () => import(/* webpackChunkName: "PropertyBasicsV2" */ '~/containers/PropertyBasicsV2')
);
const BundleLocation = React.lazy(
  () => import(/* webpackChunkName: "BundleLocation" */ '~/containers/BundleLocation')
);
const PncIntake = React.lazy(
  () => import(/* webpackChunkName: "VendorFulfillmentExperience" */ '~/containers/PncIntake')
);
const PropertyAddress = React.lazy(
  () => import(/* webpackChunkName: "PropertyAddress" */ '~/containers/PropertyAddress')
);
const Construction = React.lazy(
  () => import(/* webpackChunkName: "Construction" */ '~/containers/Construction')
);
const PreviousAddress = React.lazy(
  () => import(/* webpackChunkName: "PreviousAddress" */ '~/containers/PreviousAddress')
);
const PropertyDetails = React.lazy(
  () => import(/* webpackChunkName: "PropertyDetails" */ '~/containers/PropertyDetails')
);
const PropertyFollowUp = React.lazy(
  () => import(/* webpackChunkName: "PropertyFollowUp" */ '~/containers/PropertyFollowUp')
);
const PropertyUse = React.lazy(
  () => import(/* webpackChunkName: "PropertyUse" */ '~/containers/PropertyUse')
);
const Discounts = React.lazy(
  () => import(/* webpackChunkName: "Discounts" */ '~/containers/Discounts')
);
const HomeCoverages = React.lazy(
  () => import(/* webpackChunkName: "HomeCoverages" */ '~/containers/HomeCoverages')
);
const CurrentPolicies = React.lazy(
  () => import(/* webpackChunkName: "CurrentPolicies" */ '~/containers/CurrentPolicies')
);
const Claims = React.lazy(() => import(/* webpackChunkName: "Claims" */ '~/containers/Claims'));
const ApplicantInfo = React.lazy(
  () => import(/* webpackChunkName: "ApplicantInfo" */ '~/containers/ApplicantInfo')
);
const AdditionalApplicantInfo = React.lazy(
  () =>
    import(/* webpackChunkName: "AdditionalApplicantInfo" */ '~/containers/AdditionalApplicantInfo')
); // eslint-disable-line max-len
const Drivers = React.lazy(() => import(/* webpackChunkName: "Drivers" */ '~/containers/Drivers'));
const AdditionalDriverDetails = React.lazy(
  () =>
    import(/* webpackChunkName: "AdditionalDriverDetails" */ '~/containers/AdditionalDriverDetails')
); // eslint-disable-line max-len
const Vehicles = React.lazy(
  () => import(/* webpackChunkName: "Drivers" */ '~/containers/Vehicles')
);
const AdditionalVehicleDetails = React.lazy(
  () =>
    import(
      /* webpackChunkName: "AdditionalVehicleDetails" */ '~/containers/AdditionalVehicleDetails'
    )
); // eslint-disable-line max-len
const AutoCoverages = React.lazy(
  () => import(/* webpackChunkName: "AutoCoverages" */ '~/containers/AutoCoverages')
); // eslint-disable-line max-len
const AutoDiscounts = React.lazy(
  () => import(/* webpackChunkName: "AutoDiscounts" */ '../../containers/AutoDiscounts')
); // eslint-disable-line max-len
const ContactInfo = React.lazy(
  () => import(/* webpackChunkName: "ContactInfo" */ '~/containers/ContactInfo')
);
const PartnerOffers = React.lazy(
  () => import(/* webpackChunkName: "PartnerOffers" */ '~/containers/PartnerOffers')
);
const PartnerKinOffboard = React.lazy(
  () => import(/* webpackChunkName: "PartnerKinOffboard" */ '~/containers/PartnerKinOffboard')
);
const Confirmation = React.lazy(
  () => import(/* webpackChunkName: "Confirmation" */ '~/components/Confirmation')
);
const QuoteEstimates = React.lazy(
  () => import(/* webpackChunkName: "QuoteEstimates" */ '~/containers/QuoteEstimates')
);
const Finalization = React.lazy(
  () => import(/* webpackChunkName: "Finalization" */ '~/containers/Finalization')
);
const AutoClaims = React.lazy(
  () => import(/* webpackChunkName: "AutoClaims" */ '~/containers/AutoClaims')
);
const Offers = React.lazy(() => import(/* webpackChunkName: "Offers" */ '~/containers/Offers'));
const AutoOffers = React.lazy(
  () => import(/* webpackChunkName: "AutoOffers" */ '~/containers/AutoOffers')
);
const WelcomeBack = React.lazy(
  () => import(/* webpackChunkName: "WelcomeBack" */ '~/containers/WelcomeBack')
);
const RentersOffers = React.lazy(
  () => import(/* webpackChunkName: "RentersOffers" */ '~/containers/RentersOffers')
);

const PartnerKinStandalone = React.lazy(
  () => import(/* webpackChunkName: "PartnerKinStandalone" */ '~/containers/PartnerKinStandalone')
);

const HomeownersInsurance: React.FunctionComponent<RouteComponentProps> = ({ match, history }) => (
  <React.Fragment>
    <NavigatorHeader>
      <AccountIndicator />
    </NavigatorHeader>

    <App history={history} flow="homeowners" currentStep={getCurrentStep(steps.homeowners)}>
      {(appProps) => (
        <React.Suspense fallback={<QueryLoader delay={500} />}>
          <Switch>
            <Route
              path={`${match.url}/property-basics-v2`}
              render={(routerProps: RouteComponentProps) => (
                <PropertyBasicsV2 {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/bundle-location`}
              render={(routerProps: RouteComponentProps) => (
                <BundleLocation {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/pnc-intake`}
              render={(routerProps: RouteComponentProps) => (
                <PncIntake {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/property-address`}
              render={(routerProps: RouteComponentProps) => (
                <PropertyAddress {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/construction`}
              render={(routerProps: RouteComponentProps) => (
                <Construction {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/previous-address`}
              render={(routerProps: RouteComponentProps) => (
                <PreviousAddress {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/property-details`}
              render={(routerProps: RouteComponentProps) => (
                <PropertyDetails {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/property-followup`}
              render={(routerProps: RouteComponentProps) => (
                <PropertyFollowUp {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/property-use`}
              render={(routerProps: RouteComponentProps) => (
                <PropertyUse {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/discounts`}
              render={(routerProps: RouteComponentProps) => (
                <Discounts {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/home-coverages`}
              render={(routerProps: RouteComponentProps) => (
                <HomeCoverages {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/current-policies`}
              render={(routerProps: RouteComponentProps) => (
                <CurrentPolicies {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/claims`}
              render={(routerProps: RouteComponentProps) => (
                <Claims {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/auto-claims`}
              render={(routerProps: RouteComponentProps) => (
                <AutoClaims {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/applicant-info`}
              render={(routerProps: RouteComponentProps) => (
                <ApplicantInfo {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/additional-applicant-info`}
              render={(routerProps: RouteComponentProps) => (
                <AdditionalApplicantInfo {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/drivers`}
              render={(routerProps: RouteComponentProps) => (
                <Drivers {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/additional-driver-details`}
              render={(routerProps: RouteComponentProps) => (
                <AdditionalDriverDetails {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/vehicles`}
              render={(routerProps: RouteComponentProps) => (
                <Vehicles {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/additional-vehicle-details`}
              render={(routerProps: RouteComponentProps) => (
                <AdditionalVehicleDetails {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/auto-discounts`}
              render={(routerProps: RouteComponentProps) => (
                <AutoDiscounts {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/auto-coverages`}
              render={(routerProps: RouteComponentProps) => (
                <AutoCoverages {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/contact-info`}
              render={(routerProps: RouteComponentProps) => (
                <ContactInfo {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/partner-offers`}
              render={(routerProps: RouteComponentProps) => (
                <PartnerOffers {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/offers`}
              render={(routerProps: RouteComponentProps) => (
                <Offers {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/confirmation`}
              render={(routerProps: RouteComponentProps) => (
                <Confirmation {...routerProps} {...appProps} variant="default" />
              )}
            />
            <Route
              path={`${match.url}/estimates`}
              render={(routerProps: RouteComponentProps) => (
                <QuoteEstimates {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/finalization`}
              render={(routerProps: RouteComponentProps) => (
                <Finalization {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}/reminder`}
              render={(routerProps: RouteComponentProps) => (
                <Confirmation {...routerProps} {...appProps} variant="call_reminder" />
              )}
            />
            <Route
              path={`${match.url}/confirmation-call-scheduled`}
              render={(routerProps: RouteComponentProps) => (
                <Confirmation {...routerProps} {...appProps} variant="call_scheduled" />
              )}
            />
            <Route
              path={`${match.url}/auto-offers`}
              render={(routerProps: RouteComponentProps) => (
                <AutoOffers {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}${RENTERS_URL}`}
              render={(routerProps: RouteComponentProps) => (
                <RentersOffers {...routerProps} {...appProps} />
              )}
            />
            <Route
              path={`${match.url}${WELCOME_BACK_URL}`}
              render={(routerProps: RouteComponentProps) => (
                <WelcomeBack {...routerProps} {...appProps} />
              )}
            />
            {/* TODO: confirm url route */}
            <Route
              path={`${match.url}/im-just-kin`}
              render={(routerProps: RouteComponentProps) => (
                <PartnerKinOffboard {...routerProps} {...appProps} />
              )}
            />
            <Route path={`${match.url}/partners-kin`} render={() => <PartnerKinStandalone />} />
            <Route render={() => <div>404</div>} />
          </Switch>
        </React.Suspense>
      )}
    </App>
    <NavigatorFooter />
  </React.Fragment>
);

export default HomeownersInsurance;
